import useTypedTranslation from '@/hooks/useTypedTranslation';
import { cn } from '@/lib/utils';
import { SkuEnum } from '@/types/translations/SkuEnum';

export type TastingScope = 'upcoming' | 'past';

/**
 * Upcoming / Past, in the chip row the magazine uses for categories.
 *
 * Two chips rather than a category filter because a tasting has no categories
 * — what a visitor is choosing between is an evening they can still book and
 * one they can only read about. The split is a different query, not a filter
 * over the loaded page: past tastings are sorted the other way round and there
 * are eventually more of them than fit one page.
 */
export default function TastingScopeFilter({
  scope,
  onScopeSelect,
}: {
  scope: TastingScope;
  onScopeSelect: (scope: TastingScope) => void;
}): JSX.Element {
  const { translate } = useTypedTranslation<SkuEnum>('sku');

  const chips: { value: TastingScope; label: string }[] = [
    { value: 'upcoming', label: translate(SkuEnum['tasting.filter_upcoming']) },
    { value: 'past', label: translate(SkuEnum['tasting.filter_past']) },
  ];

  return (
    <div className="no-scrollbar tw-flex tw-gap-2 tw-overflow-x-auto tw-pb-2">
      {chips.map(chip => (
        <button
          key={chip.value}
          onClick={() => onScopeSelect(chip.value)}
          className={cn(
            'tw-whitespace-nowrap tw-rounded-full tw-px-4 tw-py-1 tw-text-sm tw-font-medium tw-transition-colors',
            {
              'tw-bg-main tw-text-white': scope === chip.value,
              'tw-bg-gray-100 tw-text-gray-600 hover:tw-bg-gray-200': scope !== chip.value,
            }
          )}
        >
          {chip.label}
        </button>
      ))}
    </div>
  );
}
